import { Component, h } from 'preact'
import Button from '../../control-components/ui-components/Button'
import Icon from '../assets'
import ColorPicker from './ColorPicker'
import InputGroup from '../../control-components/ui-components/InputGroup'
import Switcher from '../control/Switcher'
import {StylingConfigType} from './configurator'

type GenericSetting = {
  key: string,
  label: string,
  type: string
}

type ButtonStatusData = {
  background: string,
  text: string,
  border: string,
  shadow: string
}

type ButtonStatusType = 'default' | 'hover' | 'click'
type ButtonOptionsType = 'background' | 'text' | 'border' | 'shadow'

type ButtonsTheme = {
  title: string,
  description: string,
  key: string,
  'default': ButtonStatusData,
  'hover': ButtonStatusData,
  'click': ButtonStatusData,
  displayColorSection?: boolean,
  activeTab?: ButtonStatusType
}

const BUTTONS: Array<ButtonsTheme> = [
  {
    title: 'Primary button',
    description: 'Used for most common actions such as: create topic, reply, load more, login, register, etc.',
    key: 'cta',
    default: {
      background: 'config-button-cta-background-color',
      text: 'config-button-cta-color',
      border: 'config-button-cta-border-color',
      shadow: 'config-button-cta-box-shadow'
    },
    hover: {
      background: 'config-button-cta-hover-background-color',
      text: 'config-button-cta-hover-color',
      border: 'config-button-cta-hover-border-color',
      shadow: 'config-button-cta-hover-box-shadow'
    },
    click: {
      background: 'config-button-cta-active-background-color',
      text: 'config-button-cta-active-color',
      border: 'config-button-cta-active-border-color',
      shadow: 'config-button-cta-active-box-shadow'
    }
  },
  {
    title: 'Secondary button',
    description: 'Used for secondary actions: edit profile, send message.',
    key: 'secondary',
    default: {
      background: 'config-button-secondary-background-color',
      text: 'config-button-secondary-color',
      border: 'config-button-secondary-border-color',
      shadow: 'config-button-secondary-box-shadow'
    },
    hover: {
      background: 'config-button-secondary-hover-background-color',
      text: 'config-button-secondary-hover-color',
      border: 'config-button-secondary-hover-border-color',
      shadow: 'config-button-secondary-hover-box-shadow'
    },
    click: {
      background: 'config-button-secondary-active-background-color',
      text: 'config-button-secondary-active-color',
      border: 'config-button-secondary-active-border-color',
      shadow: 'config-button-secondary-active-box-shadow'
    }
  },
  {
    title: 'Toggle button (inactive)',
    description: 'Used to show the inactive status: vote for ideas, subscribe to categories, follow members.',
    key: 'toggle',
    default: {
      background: 'config-button-toggle-background-color',
      text: 'config-button-toggle-color',
      border: 'config-button-toggle-border-color',
      shadow: 'config-button-toggle-box-shadow'
    },
    hover: {
      background: 'config-button-toggle-hover-background-color',
      text: 'config-button-toggle-hover-color',
      border: 'config-button-toggle-hover-border-color',
      shadow: 'config-button-toggle-hover-box-shadow'
    },
    click: {
      background: 'config-button-toggle-active-background-color',
      text: 'config-button-toggle-active-color',
      border: 'config-button-toggle-active-border-color',
      shadow: 'config-button-toggle-active-box-shadow'
    }
  },
  {
    title: 'Toggle button (active)',
    description: 'Used to show the active status: you voted for an idea, you subscribed to a category, you followed a member.',
    key: 'toggle-on',
    default: {
      background: 'config-button-toggle-on-background-color',
      text: 'config-button-toggle-on-color',
      border: 'config-button-toggle-on-border-color',
      shadow: 'config-button-toggle-on-box-shadow'
    },
    hover: {
      background: 'config-button-toggle-on-hover-background-color',
      text: 'config-button-toggle-on-hover-color',
      border: 'config-button-toggle-on-hover-border-color',
      shadow: 'config-button-toggle-on-hover-box-shadow'
    },
    click: {
      background: 'config-button-toggle-on-active-background-color',
      text: 'config-button-toggle-on-active-color',
      border: 'config-button-toggle-on-active-border-color',
      shadow: 'config-button-toggle-on-active-box-shadow'
    }
  },
  {
    title: 'Warning button',
    description: 'Used to cancel or delete something.',
    key: 'cancel',
    default: {
      background: 'config-button-cancel-background-color',
      text: 'config-button-cancel-color',
      border: 'config-button-cancel-border-color',
      shadow: 'config-button-cancel-box-shadow'
    },
    hover: {
      background: 'config-button-cancel-hover-background-color',
      text: 'config-button-cancel-hover-color',
      border: 'config-button-cancel-hover-border-color',
      shadow: 'config-button-cancel-hover-box-shadow'
    },
    click: {
      background: 'config-button-cancel-active-background-color',
      text: 'config-button-cancel-active-color',
      border: 'config-button-cancel-active-border-color',
      shadow: 'config-button-cancel-active-box-shadow'
    }
  }
]

const GENERIC_SETTINGS: Array<GenericSetting> = [
  {
    key: 'config-button-border-radius',
    label: 'Border radius',
    type: 'pixel'
  },
  {
    key: 'config-button-border-width',
    label: 'Border width',
    type: 'pixel'
  },
  {
    key: 'config--main-button-base-font-weight',
    label: 'Font weight',
    type: 'input'
  },
  {
    key: 'config--main-button-base-texttransform',
    label: 'Uppercase',
    type: 'toggle'
  }
]

interface Props {
  updatedStyling: StylingConfigType,
  defaultStyling: StylingConfigType,
  onUpdate: (updatedStyling: StylingConfigType) => void
}

interface State {
  buttons: Array<ButtonsTheme>,
  extendedButtonColors: null | Array<ButtonsTheme>,
  enabledColorSelector: null | string
}

const BUTTON_STATUSES: Array<ButtonStatusType> = ['default', 'hover', 'click']
const BUTTON_COLOR_OPTIONS: Array<ButtonOptionsType> = ['background', 'text', 'border', 'shadow']

export default class Buttons extends Component<Props, State> {
  constructor (props: Props) {
    super(props)
    this.state = {
      buttons: BUTTONS,
      extendedButtonColors: null,
      enabledColorSelector: null
    }

    this.toggleColorSection = this.toggleColorSection.bind(this)
    this.resetButtons = this.resetButtons.bind(this)
    this.updateStyling = this.updateStyling.bind(this)
  }

  toggleColorSection (idx: number, button: ButtonsTheme) {
    const newButtons = this.state.buttons.concat([])
    newButtons[idx].displayColorSection = !button.displayColorSection
    newButtons[idx].activeTab = 'default'
    this.setState({ buttons: newButtons })
  }

  setActiveTab (idx: number, activeTab: ButtonStatusType) {
    const newButtons = this.state.buttons.concat([])
    newButtons[idx].activeTab = activeTab
    this.setState({ buttons: newButtons })
  }

  updateStyling (key: string, color: string) {
    const sameVariables: {
      [key: string]: Array<string>
    } = {
      'config-button-border-width': ['config-button-cta-border-width', 'config-button-cta-hover-border-width', 'config-button-cta-active-border-width',
        'config-button-secondary-border-width', 'config-button-secondary-hover-border-width', 'config-button-secondary-active-border-width',
        'config-button-toggle-border-width', 'config-button-toggle-hover-border-width', 'config-button-toggle-active-border-width',
        'config-button-toggle-on-border-width', 'config-button-toggle-on-hover-border-width', 'config-button-toggle-on-active-border-width',
        'config-button-cancel-border-width', 'config-button-cancel-hover-border-width', 'config-button-cancel-active-border-width'
      ],
      'config-button-border-radius': ['config-button-cta-border-radius', 'config-button-cta-hover-border-radius', 'config-button-cta-active-border-radius',
        'config-button-secondary-border-radius', 'config-button-secondary-hover-border-radius', 'config-button-secondary-active-border-radius',
        'config-button-toggle-border-radius', 'config-button-toggle-hover-border-radius', 'config-button-toggle-active-border-radius',
        'config-button-toggle-on-border-radius', 'config-button-toggle-on-hover-border-radius', 'config-button-toggle-on-active-border-radius',
        'config-button-cancel-border-radius', 'config-button-cancel-hover-border-radius', 'config-button-cancel-active-border-radius'
      ]
    }
    if (sameVariables[key]) {
      sameVariables[key].forEach(sameKey => {
        this.props.updatedStyling[sameKey] = color
      })
    }
    this.props.onUpdate({ ...this.props.updatedStyling, ...{ [key]: color } })
  }

  resetButtons () {
    this.props.onUpdate(this.props.defaultStyling)
  }

  render ({ updatedStyling, defaultStyling }: Props, { buttons, extendedButtonColors, enabledColorSelector }: State) {
    return (
      <div>
        <div className='box__pad theme-configuration_buttons-generic-settings with-border'>
          {
            GENERIC_SETTINGS.map((genericButtonSetting: GenericSetting) => {
              return (
                <div className={`theme-configuration_buttons-generic-setting ${genericButtonSetting.label.toLocaleLowerCase().split(' ').join('-')}`}>
                  <div className='theme-configuration_buttons-generic-setting_name'>{ genericButtonSetting.label }</div>
                  <div className='theme-configuration_buttons-generic-setting_configuration'>
                    {
                      genericButtonSetting.type === 'pixel' ? (
                        <div>
                          <InputGroup
                            title=''
                            type='number'
                            min={0}
                            onKeyUp={(e: {target: HTMLInputElement}) => this.updateStyling(genericButtonSetting.key, e.target.value.toString() + 'px')}
                            value={updatedStyling[genericButtonSetting.key]?.toString().replace('px', '')} />
                          <div>px</div>
                        </div>
                      ) : null
                    }
                    {
                      genericButtonSetting.type === 'input' ? (
                        <div>
                          <InputGroup
                            title=''
                            type='number'
                            min={0}
                            onKeyUp={(e: {target: HTMLInputElement}) => this.updateStyling(genericButtonSetting.key, e.target.value.toString())}
                            value={updatedStyling[genericButtonSetting.key]?.toString().replace('px', '')} />
                        </div>
                      ) : null
                    }
                    {
                      genericButtonSetting.type === 'toggle' ? (
                        <div>
                          <Switcher on={updatedStyling[genericButtonSetting.key] === 'uppercase'}
                                    onChange={() => this.updateStyling(genericButtonSetting.key, updatedStyling[genericButtonSetting.key] === 'uppercase' ? 'none' : 'uppercase')} />
                        </div>
                      ) : null
                    }
                  </div>
                </div>
              )
            })
          }
        </div>
        <div className='theme-configuration_buttons-list'>
          {
            buttons.map((button, idx) => {
              const activeTab = button.activeTab || 'default'
              return (
                <div className={`box__pad theme-configuration-list-item ${button.title.toLocaleLowerCase().split(' ').join('-')}`}>
                  <div className='theme-configuration-list-item_preview'>
                    <button className={`preview btn btn--${button.key}`}>{ button.title }</button>
                  </div>
                  <div className='theme-configuration-list-item_title' onClick={() => this.toggleColorSection(idx, button)}>
                    <Icon size={24} color='#2B3346' icon={button.displayColorSection ? 'chevronDown' : 'chevronRight'} />
                    <span>{ button.title }</span>
                  </div>
                  {
                    button.displayColorSection ? (
                      <div className='theme-configuration-list-item_settings'>
                        <p className='theme-configuration-list-item_description'> {button.description} </p>
                        <div className='theme-configuration_tabs'>
                          {
                            BUTTON_STATUSES.map(tab => (
                              <div
                                onClick={() => this.setActiveTab(idx, tab)}
                                className={`${button.activeTab === tab ? 'active-tab' : ''} theme-configuration_tab`}>
                                { tab }
                              </div>
                            ))
                          }
                        </div>
                        {
                          BUTTON_COLOR_OPTIONS.map(buttonColorOption => buttonColorOption !== 'shadow' ? (
                            <div className={`theme-configuration-list-item_setting ${buttonColorOption}-${activeTab}`}>
                              <label>{ `${buttonColorOption} Color - ${activeTab}`}</label>
                              <ColorPicker
                                color={updatedStyling[button[activeTab][buttonColorOption]]}
                                disabled={false}
                                colorKey={button[activeTab][buttonColorOption]}
                                disableColorSelector={() => this.setState({ enabledColorSelector: null })}
                                setColorSelector={() => this.setState({ enabledColorSelector: button[activeTab][buttonColorOption] })}
                                updateColor={(color: string) => this.updateStyling(button[activeTab][buttonColorOption], color)}
                                isColorSelectorEnabled={enabledColorSelector === button[activeTab][buttonColorOption]}
                              />
                            </div>
                          ) : (
                            <div className='theme-configuration-list-item_setting'>
                              <label>
                                <div>{ `${buttonColorOption} - ${activeTab}`}</div>
                                <div className='theme-configuration-list-item_setting_subtext'>Color x-axis y-axisradius, eg. rgba(0,0,0,0.2) 0  2px 4px</div>
                              </label>
                              <InputGroup
                                title=''
                                type='text'
                                placeholder='rgba(0,0,0,0.2) 0  2px 4px'
                                onKeyUp={(e: {target: HTMLInputElement}) => this.updateStyling(button[activeTab][buttonColorOption], e.target.value)}
                                value={updatedStyling[button[activeTab][buttonColorOption]] || ''} />
                            </div>
                          ))
                        }
                      </div>
                    ) : null
                  }
                </div>
              )
            })
          }
          <div className='box__pad'>
            <Button warning onClick={this.resetButtons}>
              Reset to defaults
            </Button>
          </div>
        </div>
      </div>
    )
  }
}
